import { setParentHeight } from "./utils";
import { useEffect, useRef, useState } from "react";

declare global {
  interface Document {
    frameResizingForElement?: boolean;
    frameResizingForPopup?: boolean;
  }
}

export default function useAutosize({
  dontResizeOnZeroHeight = false,
  enabled = true,
  isShown,
  label,
}: {
  dontResizeOnZeroHeight?: boolean;
  enabled?: boolean;
  isShown?: boolean;
  label?: string;
}) {
  const [elementRef, setElementRef] = useState<HTMLElement | null>(null);

  // Keep track of whether we have been displayed before.
  // Prevents a component from resizing everything down to document.body size on first render if
  // a dialog is already open.
  const [pastState, setPastState] = useState(false);

  // Mirror pastState and elementRef so the unmount cleanup below can read
  // their current values (an unmount effect's closure only sees mount-time
  // state).
  const pastStateRef = useRef(false);
  const observedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    observedElementRef.current = elementRef;
  }, [elementRef]);

  useEffect(() => {
    document.frameResizingForElement = enabled;

    return () => {
      document.frameResizingForElement = false;
    };
  }, [enabled]);

  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
    const resizeObs = new ResizeObserver((entries) => {
      if (!enabled || document.frameResizingForPopup) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);

      // A slight debounce to stop the frame from jumping up and down
      // when a page/popup fills in initially.
      resizeTimeout = setTimeout(() => {
        const cr = entries[0].contentRect;
        // Size the frame to whichever is taller, the dialog or the underlying
        // document. A position:fixed dialog adds nothing to the document
        // height, so a short popup (a two-item city list is ~170px) is clipped
        // unless the frame grows to it; flooring at the document height keeps
        // the frame from shrinking below the page behind the dialog. A prior
        // fix instead gated on height > 250, which clipped every sub-250 dialog
        // whenever the document was shorter than the popup. Zero height means
        // the dialog closed, so hand the frame back to the underlying document.
        if (cr.height) {
          setPastState(true);
          pastStateRef.current = true;
          setParentHeight(Math.max(cr.height, document.body.clientHeight));
        } else if (pastState && !dontResizeOnZeroHeight) {
          setParentHeight(document.body.clientHeight);
          setPastState(false);
          pastStateRef.current = false;
        }
      }, 100);
    });

    if (elementRef) resizeObs.observe(elementRef);
    else if (!dontResizeOnZeroHeight) resizeObs.observe(document.body);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeObs.disconnect();
    };
    // `enabled` is deliberately not a dependency: a dialog close flips it to
    // false in the same render that detaches the panel, and nothing here may
    // re-run on that render — the observer created while the dialog was open
    // must survive to catch the panel collapsing to zero height and post the
    // restore height (the pastState branch above).
  }, [elementRef, pastState, isShown]);

  // A dialog that unmounts while the frame is grown (e.g. BE's location popups
  // render as `{show && <Dialog/>}`) takes its observer with it, so the
  // collapse to zero height can never be observed — post the restore height at
  // unmount instead. Refs (not closures) keep this a no-op when a surviving
  // observer already restored the height before unmount, and the popup flag
  // defers to useAdjustFrameForPopup exactly like the observer callback does.
  useEffect(() => {
    return () => {
      if (
        !dontResizeOnZeroHeight &&
        !document.frameResizingForPopup &&
        pastStateRef.current &&
        observedElementRef.current &&
        !observedElementRef.current.isConnected
      ) {
        pastStateRef.current = false;
        setParentHeight(document.body.clientHeight);
      }
    };
  }, [dontResizeOnZeroHeight]);

  return [setElementRef];
}

export function useAutosizeDocument() {
  useEffect(() => {
    const resizeObs = new ResizeObserver((entries) => {
      if (document.frameResizingForPopup || document.frameResizingForElement)
        return;
      const cr = entries[0].contentRect;
      setParentHeight(cr.height);
    });
    resizeObs.observe(document.body);
  }, []);
}

export function useAdjustFrameForPopup({
  enabled = true,
  isShown,
  popupQuery,
  extraHeight = 10,
  label,
  fullPage = false,
  useInterval = false,
}: {
  enabled?: boolean;
  isShown: boolean | number | string | undefined;
  label?: string;
  popupQuery: string;
  extraHeight?: number;
  fullPage?: boolean;
  useInterval?: boolean;
}) {
  const [intervalVal, setIntervalVal] = useState<ReturnType<typeof setInterval> | undefined>(undefined);

  function resize() {
    let popupElem = document.querySelector(popupQuery);
    if (!popupElem) return;

    let popupHeight = popupElem.getBoundingClientRect().height + extraHeight;

    let totalHeight = popupElem.getBoundingClientRect().top + popupHeight;
    fullPage
      ? setParentHeight(Math.max(popupHeight, document.body.clientHeight))
      : setParentHeight(Math.max(totalHeight, document.body.clientHeight));
  }

  useEffect(() => {
    if (!enabled) return;

    const documentHeight = document.body.getBoundingClientRect().height;
    if (isShown) {
      document.frameResizingForPopup = true;
      if (useInterval) {
        let id = setInterval(resize, 100);
        setIntervalVal(id);
      } else {
        resize();
      }
    } else {
      document.frameResizingForPopup = false;
      setParentHeight(documentHeight);
      if (useInterval) {
        clearInterval(intervalVal);
        setIntervalVal(undefined);
      }
    }
  }, [isShown]);
}
